/**
 * The dog. Act 1 beat 4, and the first animal in this game that is not a pig.
 *
 * Spec §5.1: *"**Dogs** ignore light and cones and follow a decaying **scent
 * trail** Rosalie leaves."* That is what it does. This file is what it looks
 * like, and the two are deliberately unrelated — `src/systems/scent.ts` holds a
 * nose that knows nothing about triangles, and this holds triangles that know
 * nothing about noses.
 *
 * ── It is a soft toy, like everything else alive here ────────────────────────
 *
 * `palette.ts` says it at [[SOW]]: every animal in this world is a plush toy, and
 * the difference between them is **albedo and proportion, never material**. So
 * the dog is stitched, seamed and button-eyed exactly as Rosalie is. It is not a
 * real animal that wandered into a game about toys.
 *
 * **And it is the best-kept toy in the world.** No repairs, no bald patches, no
 * strained thread, and the glossiest buttons in the game — see [[DOG]]. Otto is a
 * torn toy sewn shut by nobody in particular; this one is looked after. That is
 * the whole characterisation and it is the reason the beat works: it is the only
 * cared-for thing in the shed, and its job is to find her.
 *
 * ── The silhouette, which is the only thing that has to survive 64 px ────────
 *
 * D-076 is the lesson from Otto and it generalises: **a species is a profile,
 * not a parts list.** Otto failed three times because his head sat level with his
 * back and the whole animal became one ramp. A dog's profile is four features and
 * nothing else matters at gameplay distance:
 *
 * 1. **Prick ears**, standing clear of the skull. They are the single most
 *    identifying shape on the animal and they are why this cannot be mistaken for
 *    any pig in the game.
 * 2. **A neck**, carrying the head visibly ABOVE the topline. Every pig here has
 *    its head at or below the line of its back.
 * 3. **The tuck.** The belly climbs hard behind the ribcage — see `TORSO`'s
 *    profile, where the deepest point is z = +0.19 and the shallowest is
 *    z = −0.10. A pig is a barrel and has no tuck at all; this one line separates
 *    the two species in shadow.
 * 4. **Leg length.** It stands 0.48 m at the withers against Rosalie's 0.32 m
 *    total (D-012), so its shoulder is above her head.
 *
 * ── What moves, and what does not ───────────────────────────────────────────
 *
 * Four pivots and no more, matching the four states beat 4 needs — walk, stop,
 * nose down, hunt. `breath` scales, `neck` drops the head to the floor, `head`
 * turns it, `ear.L`/`ear.R` swivel, `tail` and the four legs swing. Everything
 * else is welded, because a rig with a joint nothing drives is a maintenance
 * cost with no picture attached.
 */

import * as THREE from 'three';
import { DOG } from '@/render/palette';
import { collapseRigged } from '@/world/instancing';
import { surface } from './materials';
import type { AssetDefinition } from './types';

/**
 * Standing dimensions in metres, and they are real dog numbers rather than
 * convenient ones. Withers 0.48 against Rosalie's 0.32 m total height.
 *
 * **`length` is MEASURED, nose tip to tail tip, and it was wrong by 0.30 m when
 * it was derived instead.** The first value here was the body's own 1.02, which
 * is the number you get by adding up the torso and forgetting that a dog's tail
 * is a third of it again. `asset.spec.ts` caught it, exactly as it caught the
 * finisher's derived width — `footprint` frames the acceptance shots, so it has
 * to describe the mesh that exists.
 */
export const DOG_SCALE = { length: 0.97, width: 0.23, withers: 0.48, earTip: 0.73 } as const;

/**
 * One cross-section of the torso loft: a squashed ellipse at a station along Z.
 *
 * `hy` is the half-height and `y` the centre, so the **back line is `y + hy` and
 * the belly line is `y − hy`** — which is how the profile below was authored and
 * how it should be read when tuning it. Editing `y` alone moves the whole
 * section; the dog lives in the difference between the two lines.
 */
interface Ring {
  z: number;
  y: number;
  hx: number;
  hy: number;
}

/**
 * The torso, nose-forward (+Z), rear to front.
 *
 * Back line: 0.40, 0.47, 0.47, 0.46, 0.47, 0.48, 0.48, 0.48, 0.48 — level, with
 * the faintest rise into the withers. Belly line: 0.33, 0.26, 0.27, **0.29**,
 * 0.26, 0.22, **0.21**, 0.25, 0.32.
 *
 * **Those two belly numbers are the dog.** The chest is deepest at z = +0.19 and
 * the belly has climbed 8 cm by z = −0.10. Flatten that curve and this becomes a
 * short-legged pig with ears on, which is exactly the failure D-076 describes
 * from the other direction.
 */
const TORSO: readonly Ring[] = [
  { z: -0.36, y: 0.360, hx: 0.030, hy: 0.036 },
  { z: -0.30, y: 0.362, hx: 0.098, hy: 0.105 },
  { z: -0.20, y: 0.368, hx: 0.100, hy: 0.100 },
  // The tuck. Narrowest and shallowest section on the animal.
  { z: -0.10, y: 0.386, hx: 0.081, hy: 0.080 },
  { z: 0.00, y: 0.363, hx: 0.098, hy: 0.108 },
  { z: 0.10, y: 0.345, hx: 0.113, hy: 0.132 },
  // The deepest point of the chest, and the widest.
  { z: 0.19, y: 0.338, hx: 0.112, hy: 0.140 },
  { z: 0.26, y: 0.360, hx: 0.093, hy: 0.115 },
  // Withers, tapering into the neck root.
  { z: 0.31, y: 0.400, hx: 0.068, hy: 0.078 },
];

/**
 * Where the neck leaves the withers, and where the skull sits on top of it.
 *
 * **The first version of these two points put the skull 0.12 m further forward
 * and the neck came out at 53° from horizontal, and it was wrong in the render
 * in a way it was not wrong on paper.** The head was well clear of the topline —
 * rule 2 in the header was satisfied by the numbers — but it was clear of it
 * *forward*, so the whole neck-and-muzzle assembly read as one horizontal spar
 * and the animal became a greyhound at a gallop. What makes a dog look like a
 * dog standing still is that the neck is nearly **upright**: the head is over
 * the shoulders, not in front of them.
 *
 * 71° now, and the muzzle is shorter and drops rather than running level. Check
 * this against the turnaround, never against the arithmetic — the arithmetic
 * agreed with itself the first time.
 */
const NECK_ROOT = new THREE.Vector3(0, 0.418, 0.296);
const NECK_TOP = new THREE.Vector3(0, 0.568, 0.348);

function mesh(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const result = new THREE.Mesh(geometry, material);
  result.name = name;
  result.castShadow = true;
  result.receiveShadow = true;
  return result;
}

function pivot(name: string, x: number, y: number, z: number): THREE.Object3D {
  const node = new THREE.Object3D();
  node.name = name;
  node.position.set(x, y, z);
  return node;
}

/**
 * Loft a closed tube through the rings, capping both ends with a fan.
 *
 * One surface rather than a chain of primitives, for the same reason Rosalie's
 * torso is one loft (the G1 fix, see `SurfaceOptions.seam`): the felt seam is
 * drawn down the object's local X = 0, so a body assembled from three spheres
 * would get three separate seams that do not line up. A dog's stitching runs
 * from its tail to its throat in one line or it is not a toy.
 */
function loft(rings: readonly Ring[], segments: number): THREE.BufferGeometry {
  const positions: number[] = [];
  const indices: number[] = [];

  for (const ring of rings) {
    for (let j = 0; j <= segments; j += 1) {
      const theta = (j / segments) * Math.PI * 2;
      positions.push(ring.hx * Math.sin(theta), ring.y + ring.hy * Math.cos(theta), ring.z);
    }
  }

  for (let i = 0; i < rings.length - 1; i += 1) {
    for (let j = 0; j < segments; j += 1) {
      const a = i * (segments + 1) + j;
      const b = a + segments + 1;
      indices.push(a, b, a + 1, a + 1, b, b + 1);
    }
  }

  const first = rings[0];
  const last = rings[rings.length - 1];
  if (first === undefined || last === undefined) throw new Error('dog: torso loft needs rings');

  const backCentre = positions.length / 3;
  positions.push(0, first.y, first.z);
  for (let j = 0; j < segments; j += 1) indices.push(backCentre, j, j + 1);

  const frontRing = (rings.length - 1) * (segments + 1);
  const frontCentre = positions.length / 3;
  positions.push(0, last.y, last.z);
  for (let j = 0; j < segments; j += 1) indices.push(frontCentre, frontRing + j + 1, frontRing + j);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

/**
 * A tapered cylinder between two points.
 *
 * Limbs are authored as "from here to there" rather than as a length plus two
 * Euler angles, because a dog's hind leg is three segments at three different
 * angles and the angles are not the thing anybody wants to tune — the hock
 * position is. Writing the joints as points keeps the leg readable.
 */
function segment(
  from: THREE.Vector3,
  to: THREE.Vector3,
  fromRadius: number,
  toRadius: number,
  material: THREE.Material,
  name: string,
): THREE.Mesh {
  const axis = new THREE.Vector3().subVectors(to, from);
  const length = axis.length();
  const geometry = new THREE.CylinderGeometry(toRadius, fromRadius, length, 6, 1);
  geometry.translate(0, length / 2, 0);
  const bone = mesh(geometry, material, name);
  bone.position.copy(from);
  bone.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), axis.normalize());
  return bone;
}

/**
 * A front leg: near enough straight, which is what a dog's foreleg actually is.
 *
 * The pivot sits at the shoulder so a walk cycle is one rotation on X. Local
 * space, so the leg hangs from (0,0,0) down to the floor at −0.365.
 */
function buildForeleg(side: 1 | -1, fabric: THREE.Material, sock: THREE.Material): THREE.Object3D {
  // Forward of the first attempt's 0.185, so the leg drops from under the deepest
  // point of the chest instead of from behind it. A foreleg set back leaves the
  // brisket overhanging nothing, which is what made the first render's front end
  // look like it was falling over.
  const leg = pivot(side > 0 ? 'leg.FL' : 'leg.FR', side * 0.078, 0.365, 0.205);
  const shoulder = new THREE.Vector3(0, 0, 0);
  const elbow = new THREE.Vector3(0, -0.160, -0.004);
  const pastern = new THREE.Vector3(0, -0.320, 0.006);
  leg.add(segment(shoulder, elbow, 0.038, 0.026, fabric, 'upper'));
  leg.add(segment(elbow, pastern, 0.024, 0.020, fabric, 'lower'));
  leg.add(segment(pastern, new THREE.Vector3(0, -0.352, 0.008), 0.021, 0.023, sock, 'sock'));

  const paw = mesh(new THREE.SphereGeometry(0.030, 6, 4), sock, 'paw');
  paw.scale.set(1, 0.62, 1.25);
  paw.position.set(0, -0.348, 0.018);
  leg.add(paw);
  return leg;
}

/**
 * A hind leg, and it is the one place a dog cannot be built like a table.
 *
 * Femur forward-and-down, tibia back-and-down to a hock that sits BEHIND the
 * hip, then a near-vertical metatarsus to the floor. That backward kink is the
 * second-strongest quadruped cue after the ears, and building the hind leg
 * straight — which is the obvious thing — makes the animal read as furniture.
 */
function buildHindleg(side: 1 | -1, fabric: THREE.Material, sock: THREE.Material): THREE.Object3D {
  const leg = pivot(side > 0 ? 'leg.BL' : 'leg.BR', side * 0.088, 0.360, -0.240);
  const hip = new THREE.Vector3(0, 0, 0);
  const stifle = new THREE.Vector3(0, -0.145, 0.055);
  const hock = new THREE.Vector3(0, -0.255, -0.045);
  const pastern = new THREE.Vector3(0, -0.322, -0.030);
  leg.add(segment(hip, stifle, 0.048, 0.030, fabric, 'femur'));
  leg.add(segment(stifle, hock, 0.028, 0.021, fabric, 'tibia'));
  leg.add(segment(hock, pastern, 0.021, 0.021, sock, 'metatarsus'));

  const paw = mesh(new THREE.SphereGeometry(0.029, 6, 4), sock, 'paw');
  paw.scale.set(1, 0.62, 1.25);
  paw.position.set(0, -0.334, -0.020);
  leg.add(paw);
  return leg;
}

/**
 * The tail: low-set, **hanging**, with the tip curling forward.
 *
 * Not a plume, not over the back, and — the first attempt's mistake — not held
 * straight out behind. A tail on the horizontal is a pointer on a scent, which
 * is a specific and wrong thing to say about a dog that is walking at heel; on
 * the turnaround it also read as a rod welded to the rump rather than as part of
 * the animal. A working dog off duty carries it down, and the moment it comes up
 * is a moment this beat has not authored.
 */
function buildTail(fabric: THREE.Material, tip: THREE.Material): THREE.Object3D {
  // On the croup and slightly inside the rump, which is where a tail attaches.
  const node = pivot('tail', 0, 0.395, -0.330);
  const rings: Ring[] = [
    // **It leaves the body BACKWARD before it drops**, and that is not a
    // stylistic note. The second attempt hung it almost straight down from
    // z = −0.345 and the whole tail disappeared: it lay flat against the rump,
    // added 0.07 m to the animal's length, and could not be found in the
    // silhouette at all. A tail is only visible in profile if it clears the
    // hindquarters first. Measured length fell from 1.32 m to 0.97 m and that
    // number was the tell — it is the cheapest check there is for "a limb went
    // inside the body".
    { z: 0.0, y: 0.0, hx: 0.026, hy: 0.026 },
    { z: -0.075, y: -0.012, hx: 0.024, hy: 0.024 },
    { z: -0.135, y: -0.075, hx: 0.020, hy: 0.020 },
    { z: -0.165, y: -0.160, hx: 0.015, hy: 0.015 },
    // The tip curls forward again, which is what stops a hanging tail reading
    // as a rope. It is also the only asymmetry on the animal's midline.
    { z: -0.150, y: -0.235, hx: 0.010, hy: 0.010 },
  ];
  node.add(mesh(loft(rings, 6), fabric, 'tail-shaft'));

  const white = mesh(new THREE.SphereGeometry(0.015, 6, 4), tip, 'tail-tip');
  white.position.set(0, -0.231, -0.148);
  node.add(white);
  return node;
}

/** Prick ears. The most identifying shape on the animal — see the header. */
function buildEar(side: 1 | -1, fabric: THREE.Material): THREE.Object3D {
  const node = pivot(side > 0 ? 'ear.L' : 'ear.R', side * 0.043, 0.048, -0.020);
  const cone = new THREE.ConeGeometry(0.040, 0.098, 4, 1);
  cone.translate(0, 0.049, 0);
  const shell = mesh(cone, fabric, 'ear-shell');
  // Flattened front to back and canted outward: a cone standing straight up
  // reads as a horn. The lean is what makes two of them read as a pair of ears.
  shell.scale.set(1, 1, 0.55);
  shell.rotation.set(-0.12, 0, side * 0.26);
  node.add(shell);
  return node;
}

function buildHead(fabric: THREE.Material, shadow: THREE.Material, marking: THREE.Material, button: THREE.Material): THREE.Object3D {
  const head = pivot('head', NECK_TOP.x, NECK_TOP.y, NECK_TOP.z);

  const skull = mesh(new THREE.SphereGeometry(1, 8, 6), fabric, 'skull');
  skull.scale.set(0.068, 0.070, 0.082);
  skull.position.set(0, 0.028, 0.036);
  head.add(skull);

  // A tapering muzzle that DROPS away from the skull rather than continuing it.
  // A muzzle level with the skull is a bear; a short one is a pug; a long level
  // one — which is what the first attempt built — is a rat. The drop is 0.028 m
  // over 0.088 m of length and it is the whole difference.
  head.add(segment(
    new THREE.Vector3(0, 0.014, 0.078),
    new THREE.Vector3(0, -0.014, 0.166),
    0.046,
    0.030,
    fabric,
    'muzzle',
  ));

  const nose = mesh(new THREE.SphereGeometry(0.025, 6, 4), button, 'nose');
  nose.scale.set(1, 0.86, 0.78);
  nose.position.set(0, -0.016, 0.176);
  head.add(nose);

  const blaze = mesh(new THREE.SphereGeometry(1, 6, 4), marking, 'blaze');
  blaze.scale.set(0.016, 0.028, 0.085);
  blaze.position.set(0, 0.042, 0.082);
  head.add(blaze);

  for (const side of [1, -1] as const) {
    const eye = mesh(new THREE.SphereGeometry(0.014, 5, 4), button, side > 0 ? 'eye.L' : 'eye.R');
    eye.scale.z = 0.7;
    eye.position.set(side * 0.042, 0.040, 0.090);
    head.add(eye);
    head.add(buildEar(side, fabric));
  }

  const cheek = mesh(new THREE.SphereGeometry(1, 6, 4), shadow, 'jaw');
  cheek.scale.set(0.038, 0.024, 0.056);
  cheek.position.set(0, -0.026, 0.100);
  head.add(cheek);

  return head;
}

/**
 * The collar: the only thing any animal in this game wears.
 *
 * Rosalie has nothing, Otto has repairs, the finishers have nothing. Somebody
 * bought this dog a collar, and that fact is worth more to the beat than any
 * amount of menace would be. See [[DOG]].
 */
function buildCollar(webbing: THREE.Material, buckle: THREE.Material): THREE.Object3D {
  const node = new THREE.Object3D();
  node.name = 'collar';
  const axis = new THREE.Vector3().subVectors(NECK_TOP, NECK_ROOT).normalize();
  node.position.copy(NECK_ROOT).addScaledVector(axis, 0.072);
  node.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), axis);

  const strap = mesh(new THREE.TorusGeometry(0.058, 0.012, 4, 12), webbing, 'strap');
  node.add(strap);

  const clasp = mesh(new THREE.BoxGeometry(0.028, 0.020, 0.014), buckle, 'buckle');
  clasp.position.set(0.058, 0, 0);
  node.add(clasp);

  const ring = mesh(new THREE.TorusGeometry(0.017, 0.005, 4, 8), buckle, 'ring');
  ring.position.set(0, -0.062, 0.004);
  ring.rotation.y = Math.PI / 2;
  node.add(ring);

  return node;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'dog';

  const fabric = surface('fabric', DOG.fabric, {
    seam: true,
    // Finer than Otto's and than Rosalie's. Nothing on this toy has been mended
    // and its stitching is machine-neat; a coarse seam here would say the
    // opposite of everything [[DOG]] is for.
    seamWidth: 0.005,
    seamColour: DOG.seam,
  });
  const shadow = surface('fabric', DOG.fabricShadow);
  const marking = surface('fabric', DOG.marking);
  const button = surface('fabric', DOG.button);
  const webbing = surface('fabric', DOG.collar);
  const buckle = surface('metal', DOG.buckle);

  // Y-only scale, at the floor and unrotated, exactly as `finisher.ts` does it:
  // the contact plane stays put while the ribcage expands upward, so breathing
  // never sinks the animal into the concrete.
  const breath = pivot('breath', 0, 0, 0);
  group.add(breath);

  const stance = pivot('stance', 0, 0, 0);
  breath.add(stance);

  stance.add(mesh(loft(TORSO, 12), fabric, 'body'));

  // One pivot for the whole neck, so `nose down` is a single rotation and the
  // head, muzzle, ears and collar all come with it. The dog spends most of the
  // beat with its nose on the floor; that has to be one number.
  const neck = pivot('neck', NECK_ROOT.x, NECK_ROOT.y, NECK_ROOT.z);
  stance.add(neck);
  neck.add(segment(
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3().subVectors(NECK_TOP, NECK_ROOT),
    0.064,
    0.050,
    fabric,
    'neck-shaft',
  ));

  const head = buildHead(fabric, shadow, marking, button);
  head.position.sub(NECK_ROOT);
  neck.add(head);
  const collar = buildCollar(webbing, buckle);
  collar.position.sub(NECK_ROOT);
  neck.add(collar);

  const bib = mesh(new THREE.SphereGeometry(1, 6, 4), marking, 'bib');
  bib.scale.set(0.052, 0.088, 0.034);
  bib.position.set(0, 0.302, 0.302);
  stance.add(bib);

  for (const side of [1, -1] as const) {
    stance.add(buildForeleg(side, fabric, marking));
    stance.add(buildHindleg(side, fabric, marking));
  }
  stance.add(buildTail(fabric, marking));

  // Measured, not derived. `footprint` frames the acceptance shots, and
  // `finisher.ts` records what happens when the declared box is an argument
  // about the sketch rather than a measurement of the mesh: `asset.spec.ts`
  // rejects it, correctly. Paws, ear tips and the tail's lift are all properties
  // of geometry that exists.
  group.updateMatrixWorld(true);
  const bounds = new THREE.Box3().setFromObject(group);
  stance.position.y -= bounds.min.y;
  stance.position.x -= (bounds.min.x + bounds.max.x) / 2;
  stance.position.z -= (bounds.min.z + bounds.max.z) / 2;

  const parts = findDogParts(group);
  collapseRigged(group, [
    parts.breath,
    stance,
    parts.neck,
    parts.head,
    ...parts.ears,
    parts.tail,
    ...parts.legs,
  ]);

  return group;
}

export interface DogParts {
  /** Scale on Y only. The asset's idle channel. */
  breath: THREE.Object3D;
  /** Rotate on X to put the nose on the floor. Carries head, ears and collar. */
  neck: THREE.Object3D;
  /** Rotate to look. Local to `neck`. */
  head: THREE.Object3D;
  ears: readonly [THREE.Object3D, THREE.Object3D];
  tail: THREE.Object3D;
  /** Front-left, front-right, back-left, back-right. Rotate on X to walk. */
  legs: readonly [THREE.Object3D, THREE.Object3D, THREE.Object3D, THREE.Object3D];
}

function required(root: THREE.Object3D, name: string): THREE.Object3D {
  const found = root.getObjectByName(name);
  if (!found) throw new Error(`dog: rig node "${name}" missing — was the mesh renamed?`);
  return found;
}

export function findDogParts(root: THREE.Object3D): DogParts {
  return {
    breath: required(root, 'breath'),
    neck: required(root, 'neck'),
    head: required(root, 'head'),
    ears: [required(root, 'ear.L'), required(root, 'ear.R')],
    tail: required(root, 'tail'),
    legs: [
      required(root, 'leg.FL'),
      required(root, 'leg.FR'),
      required(root, 'leg.BL'),
      required(root, 'leg.BR'),
    ],
  };
}

export const dogAsset: AssetDefinition = {
  id: 'dog',
  label: 'Farm dog',
  description: 'The shed dog. A plush toy with no repairs on it, because somebody looks after this one.',
  footprint: { width: DOG_SCALE.width, height: DOG_SCALE.earTip, length: DOG_SCALE.length },
  create: build,
};
